import api from './client';

// Enhanced nutritional contribution interface with comprehensive nutrients
export interface NutritionalContribution {
  // Macronutrients (g/kg)
  protein: number;
  fat: number;
  fiber: number;
  carbohydrate: number;
  moisture: number;
  calories?: number;
  
  // Major minerals (g/kg)
  calcium?: number;
  phosphorus?: number;
  magnesium?: number;
  potassium?: number;
  sodium?: number;
  
  // Trace minerals (mg/kg)
  iron?: number;
  zinc?: number;
  manganese?: number;
  copper?: number;
  selenium?: number;
  
  // Fat-soluble vitamins
  vitaminA?: number;    // IU/kg
  vitaminD3?: number;   // IU/kg
  vitaminE?: number;    // IU/kg
  vitaminK?: number;    // mg/kg
  
  // Water-soluble vitamins (mg/kg)
  thiamine?: number;     // B1
  riboflavin?: number;   // B2
  niacin?: number;       // B3
  pantothenicAcid?: number; // B5
  pyridoxine?: number;   // B6
  biotin?: number;
  folicAcid?: number;
  vitaminB12?: number;
  choline?: number;
}

export interface FeedMixComponent {
  feedStockId: string;
  feedStockName: string;
  quantity: number;
  unit: string;
  percentage: number;
  nutritionalContribution: NutritionalContribution;
}

// Enhanced daily nutritional summary with comprehensive nutrients
export interface DailyNutritionalSummary {
  totalQuantity: number;
  totalCalories: number;
  
  // Macronutrients as percentages
  proteinPercentage: number;
  fatPercentage: number;
  fiberPercentage: number;
  carbohydratePercentage: number;
  moisturePercentage: number;
  
  // Macronutrients as grams
  proteinGrams: number;
  fatGrams: number;
  fiberGrams: number;
  carbohydrateGrams: number;
  moistureGrams: number;
  
  // Major minerals (mg total)
  calciumMg?: number;
  phosphorusMg?: number;
  magnesiumMg?: number;
  potassiumMg?: number;
  sodiumMg?: number;
  
  // Trace minerals (mg total)
  ironMg?: number;
  zincMg?: number;
  manganeseMg?: number;
  copperMg?: number;
  seleniumMg?: number;
  
  // Fat-soluble vitamins (IU or mg total)
  vitaminA_IU?: number;
  vitaminD3_IU?: number;
  vitaminE_IU?: number;
  vitaminK_mg?: number;
  
  // Water-soluble vitamins (mg total)
  thiamine_mg?: number;
  riboflavin_mg?: number;
  niacin_mg?: number;
  pantothenicAcid_mg?: number;
  pyridoxine_mg?: number;
  biotin_mg?: number;
  folicAcid_mg?: number;
  vitaminB12_mg?: number;
  choline_mg?: number;
  
  qualityScore: number;
  recommendations?: string[];
  
  // Nutritional adequacy indicators
  adequacyScores?: {
    macronutrients: number;
    minerals: number;
    vitamins: number;
    overall: number;
  };
  
  // Deficiency/excess alerts
  nutritionalAlerts?: Array<{
    nutrient: string;
    type: 'deficiency' | 'excess' | 'optimal';
    severity: 'low' | 'medium' | 'high';
    currentValue: number;
    targetRange: { min: number; max: number };
    unit: string;
  }>;
}

export interface DailyFeedMix {
  id: string;
  feedDate: string;
  location?: string;
  operator?: string;
  feedComponents: FeedMixComponent[];
  nutritionalSummary: DailyNutritionalSummary;
  totalQuantity: number;
  totalCost?: number;
  notes?: string;
  status: string;
  metadata?: {
    birdCount?: number;
    feedingTimes?: string[];
    weatherConditions?: string;
    specialNotes?: string;
    approved?: boolean;
    approvedBy?: string;
    approvedDate?: string;
    tags?: string[];
    source?: string;
    readyForDispense?: boolean;
    optimizationScore?: number;
    dispensedAt?: string;
    dispensedBy?: string;
    dispenseCage?: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface CreateDailyFeedMixDto {
  feedDate: string;
  feedComponents: Array<{
    feedStockId: string;
    quantity: number;
    notes?: string;
  }>;
  location?: string;
  operator?: string;
  notes?: string;
  birdCount?: number;
  feedingTimes?: string[];
  weatherConditions?: string;
  specialNotes?: string;
  tags?: string[];
}

export interface NutritionalTrendData {
  date: string;
  
  // Macronutrients (percentages)
  proteinPercentage: number;
  fatPercentage: number;
  fiberPercentage: number;
  carbohydratePercentage: number;
  moisturePercentage: number;
  
  // Feed metrics
  totalQuantity: number;
  qualityScore: number;
  totalCalories: number;
  
  // Major minerals (mg/kg)
  calciumMg?: number;
  phosphorusMg?: number;
  magnesiumMg?: number;
  potassiumMg?: number;
  sodiumMg?: number;
  
  // Trace minerals (mg/kg)
  ironMg?: number;
  zincMg?: number;
  manganeseMg?: number;
  copperMg?: number;
  seleniumMg?: number;
  
  // Fat-soluble vitamins
  vitaminA_IU?: number;
  vitaminD3_IU?: number;
  vitaminE_IU?: number;
  vitaminK_mg?: number;
  
  // Water-soluble vitamins (mg/kg)
  thiamine_mg?: number;
  riboflavin_mg?: number;
  niacin_mg?: number;
  pantothenicAcid_mg?: number;
  pyridoxine_mg?: number;
  biotin_mg?: number;
  folicAcid_mg?: number;
  vitaminB12_mg?: number;
  choline_mg?: number;
  
  // Adequacy scores
  macronutrientAdequacy?: number;
  mineralAdequacy?: number;
  vitaminAdequacy?: number;
  overallAdequacy?: number;
}

export interface NutritionalAnalytics {
  period: { start: string; end: string };
  averages: {
    proteinPercentage: number;
    fatPercentage: number;
    fiberPercentage: number;
    carbohydratePercentage: number;
    moisturePercentage: number;
    qualityScore: number;
    dailyQuantity: number;
    dailyCalories: number;
  };
  trends: {
    protein: 'improving' | 'declining' | 'stable';
    fat: 'improving' | 'declining' | 'stable';
    fiber: 'improving' | 'declining' | 'stable';
    carbohydrate: 'improving' | 'declining' | 'stable';
    quality: 'improving' | 'declining' | 'stable';
  };
  variability: {
    proteinStdDev: number;
    fatStdDev: number;
    fiberStdDev: number;
    carbohydrateStdDev: number;
    qualityStdDev: number;
  };
  recommendations: string[];
  alerts: Array<{
    type: 'deficiency' | 'excess' | 'imbalance';
    nutrient: string;
    severity: 'low' | 'medium' | 'high';
    message: string;
  }>;
}

// Comprehensive nutritional requirements for parrots
export interface NutritionalRequirements {
  // Macronutrients (g/kg)
  protein: { min: number; max: number; optimal: number };
  fat: { min: number; max: number; optimal: number };
  fiber: { min: number; max: number; optimal: number };
  carbohydrate: { min: number; max: number; optimal: number };
  moisture: { min: number; max: number; optimal: number };
  
  // Major minerals (g/kg)
  calcium: { min: number; max: number; optimal: number };
  phosphorus: { min: number; max: number; optimal: number };
  magnesium: { min: number; max: number; optimal: number };
  potassium: { min: number; max: number; optimal: number };
  sodium: { min: number; max: number; optimal: number };
  
  // Trace minerals (mg/kg)
  iron: { min: number; max: number; optimal: number };
  zinc: { min: number; max: number; optimal: number };
  manganese: { min: number; max: number; optimal: number };
  copper: { min: number; max: number; optimal: number };
  selenium: { min: number; max: number; optimal: number };
  
  // Fat-soluble vitamins
  vitaminA: { min: number; max: number; optimal: number; unit: 'IU/kg' };
  vitaminD3: { min: number; max: number; optimal: number; unit: 'IU/kg' };
  vitaminE: { min: number; max: number; optimal: number; unit: 'IU/kg' };
  vitaminK: { min: number; max: number; optimal: number; unit: 'mg/kg' };
  
  // Water-soluble vitamins (mg/kg)
  thiamine: { min: number; max: number; optimal: number };
  riboflavin: { min: number; max: number; optimal: number };
  niacin: { min: number; max: number; optimal: number };
  pantothenicAcid: { min: number; max: number; optimal: number };
  pyridoxine: { min: number; max: number; optimal: number };
  biotin: { min: number; max: number; optimal: number };
  folicAcid: { min: number; max: number; optimal: number };
  vitaminB12: { min: number; max: number; optimal: number };
  choline: { min: number; max: number; optimal: number };
  
  // Metadata
  birdSpecies?: string;
  lifestage?: 'breeding' | 'maintenance' | 'molting' | 'juvenile';
  minCaloriesPerKg?: number;
  
  // Compatibility with existing system
  minProteinPercentage: number;
  maxProteinPercentage: number;
  minFatPercentage: number;
  maxFatPercentage: number;
  maxFiberPercentage: number;
  maxMoisturePercentage: number;
  minCalciumPercentage?: number;
  minPhosphorusPercentage?: number;
}

// Parrot-specific nutritional requirements based on the provided specifications
export const PARROT_NUTRITIONAL_REQUIREMENTS: NutritionalRequirements = {
  // Macronutrients (g/kg)
  protein: { min: 140, max: 180, optimal: 160 },
  fat: { min: 40, max: 80, optimal: 60 },
  fiber: { min: 20, max: 60, optimal: 40 },
  carbohydrate: { min: 200, max: 600, optimal: 400 },
  moisture: { min: 80, max: 120, optimal: 100 },
  
  // Major minerals (g/kg)
  calcium: { min: 8, max: 12, optimal: 10 },
  phosphorus: { min: 4, max: 6, optimal: 5 },
  magnesium: { min: 1.2, max: 1.8, optimal: 1.5 },
  potassium: { min: 4, max: 6, optimal: 5 },
  sodium: { min: 1.2, max: 1.8, optimal: 1.5 },
  
  // Trace minerals (mg/kg)
  iron: { min: 70, max: 110, optimal: 90 },
  zinc: { min: 50, max: 70, optimal: 60 },
  manganese: { min: 60, max: 80, optimal: 70 },
  copper: { min: 10, max: 14, optimal: 12 },
  selenium: { min: 0.2, max: 0.3, optimal: 0.25 },
  
  // Fat-soluble vitamins
  vitaminA: { min: 8000, max: 12000, optimal: 10000, unit: 'IU/kg' },
  vitaminD3: { min: 800, max: 1200, optimal: 1000, unit: 'IU/kg' },
  vitaminE: { min: 60, max: 90, optimal: 75, unit: 'IU/kg' },
  vitaminK: { min: 1.2, max: 1.8, optimal: 1.5, unit: 'mg/kg' },
  
  // Water-soluble vitamins (mg/kg)
  thiamine: { min: 5.6, max: 8.4, optimal: 7 },
  riboflavin: { min: 7.2, max: 10.8, optimal: 9 },
  niacin: { min: 52, max: 78, optimal: 65 },
  pantothenicAcid: { min: 12, max: 18, optimal: 15 },
  pyridoxine: { min: 4.8, max: 7.2, optimal: 6 },
  biotin: { min: 0.24, max: 0.36, optimal: 0.3 },
  folicAcid: { min: 1.2, max: 1.8, optimal: 1.5 },
  vitaminB12: { min: 0.012, max: 0.018, optimal: 0.015 },
  choline: { min: 1000, max: 1500, optimal: 1250 },
  
  // Metadata
  birdSpecies: 'Parrot (General)',
  lifestage: 'maintenance',
  minCaloriesPerKg: 3500,
  
  // Compatibility with existing system (converted to percentages)
  minProteinPercentage: 14,
  maxProteinPercentage: 18,
  minFatPercentage: 4,
  maxFatPercentage: 8,
  maxFiberPercentage: 6,
  maxMoisturePercentage: 12,
  minCalciumPercentage: 0.8,
  minPhosphorusPercentage: 0.4,
};

export interface OptimizedFeedMix {
  optimizedMix: FeedMixComponent[];
  nutritionalSummary: DailyNutritionalSummary;
  optimization: {
    goal: string;
    score: number;
    costPerKg: number;
    improvements: string[];
  };
}

export interface ReportHistoryParams {
  location?: string;
  startDate: string;
  endDate: string;
}

export interface ReportGenerationParams {
  type: string;
  format: 'pdf' | 'excel' | 'csv' | 'json';
  startDate: string;
  endDate: string;
  location?: string;
}

export interface ScheduledReportParams {
  location?: string;
}

export interface ReportScheduleData {
  type: string;
  frequency: 'daily' | 'weekly' | 'monthly';
  recipients: string[];
  format: 'pdf' | 'excel' | 'csv' | 'json';
  enabled: boolean;
  location?: string;
}

export interface FeedQuantityEstimation {
  estimatedDailyFeedKg: number;
  totalFlockWeightKg: number;
  birdCount: number;
  calculationMethod: string;
  weightBreakdown: Array<{
    speciesName: string;
    count: number;
    totalWeightKg: number;
    feedContributionKg: number;
  }>;
}

export interface NutritionalDashboardData {
  period: { days: number; startDate: string; endDate: string };
  trends: NutritionalTrendData[];
  analytics: NutritionalAnalytics;
  dashboard: {
    totalMixes: number;
    recentMixes: number;
    pendingApprovals: number;
    qualityIssues: number;
    averageQualityScore: number;
    nutritionalStability: {
      protein: string;
      fat: string;
      fiber: string;
      carbohydrate: string;
    };
  };
  alerts: Array<{
    type: 'deficiency' | 'excess' | 'imbalance';
    nutrient: string;
    severity: 'low' | 'medium' | 'high';
    message: string;
  }>;
  recommendations: string[];
  feedQuantityEstimation: FeedQuantityEstimation;
}

// API Functions
export const nutritionApi = {
  // Daily Feed Mix Management
  async getDailyFeedMixes(filters?: {
    startDate?: string;
    endDate?: string;
    location?: string;
    status?: string;
  }): Promise<DailyFeedMix[]> {
    try {
      const params = new URLSearchParams();
      if (filters?.startDate) params.append('startDate', filters.startDate);
      if (filters?.endDate) params.append('endDate', filters.endDate);
      if (filters?.location) params.append('location', filters.location);
      if (filters?.status) params.append('status', filters.status);

      const response = await api.get<DailyFeedMix[]>(`/feed-stock/nutrition/daily-mixes?${params}`);
      
      if (!Array.isArray(response.data)) {
        throw new Error('Invalid response format: expected array of daily mixes');
      }
      
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async getDailyFeedMix(id: string): Promise<DailyFeedMix> {
    const response = await api.get<DailyFeedMix>(`/feed-stock/nutrition/daily-mixes/${id}`);
    return response.data;
  },

  async getDailyFeedMixByDate(date: string, location?: string): Promise<DailyFeedMix | null> {
    const params = new URLSearchParams();
    if (location) params.append('location', location);

    const response = await api.get<DailyFeedMix | null>(`/feed-stock/nutrition/daily-mixes/date/${date}?${params}`);
    return response.data;
  },

  async createDailyFeedMix(data: CreateDailyFeedMixDto): Promise<DailyFeedMix> {
    const response = await api.post<DailyFeedMix>('/feed-stock/nutrition/daily-mixes', data);
    return response.data;
  },

  async updateDailyFeedMix(id: string, data: Partial<CreateDailyFeedMixDto>): Promise<DailyFeedMix> {
    const response = await api.put<DailyFeedMix>(`/feed-stock/nutrition/daily-mixes/${id}`, data);
    return response.data;
  },

  async approveDailyFeedMix(id: string, approved: boolean, approvedBy?: string, approvalNotes?: string): Promise<DailyFeedMix> {
    const response = await api.put<DailyFeedMix>(`/feed-stock/nutrition/daily-mixes/${id}/approve`, {
      approved,
      approvedBy,
      approvalNotes
    });
    return response.data;
  },

  async deleteDailyFeedMix(id: string): Promise<void> {
    await api.delete(`/feed-stock/nutrition/daily-mixes/${id}`);
  },

  // Analytics and Trends
  async getNutritionalTrends(filters?: {
    startDate?: string;
    endDate?: string;
    location?: string;
    feedStockIds?: string[];
    period?: 'daily' | 'weekly' | 'monthly';
    metrics?: string[];
  }): Promise<{ data: NutritionalTrendData[]; analytics: NutritionalAnalytics }> {
    try {
      const params = new URLSearchParams();
      if (filters?.startDate) params.append('startDate', filters.startDate);
      if (filters?.endDate) params.append('endDate', filters.endDate);
      if (filters?.location) params.append('location', filters.location);
      if (filters?.period) params.append('period', filters.period);

      const response = await api.get<{ data: NutritionalTrendData[]; analytics: NutritionalAnalytics }>(`/feed-stock/nutrition/trends?${params}`);
      
      if (!response.data || typeof response.data !== 'object') {
        throw new Error('Invalid response format: expected trends object');
      }
      
      if (!Array.isArray(response.data.data)) {
        throw new Error('Invalid response format: expected trends data array');
      }
      
      if (!response.data.analytics || typeof response.data.analytics !== 'object') {
        throw new Error('Invalid response format: expected analytics object');
      }
      
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async getNutritionalDashboard(days: number = 30, location?: string): Promise<NutritionalDashboardData> {
    try {
      const params = new URLSearchParams();
      params.append('days', days.toString());
      if (location) params.append('location', location);

      const response = await api.get<{
        period: { days: number; startDate: string; endDate: string };
        trends: NutritionalTrendData[];
        analytics: NutritionalAnalytics;
        dashboard: {
          totalMixes: number;
          recentMixes: number;
          pendingApprovals: number;
          qualityIssues: number;
          averageQualityScore: number;
          nutritionalStability: {
            protein: string;
            fat: string;
            fiber: string;
            carbohydrate: string;
          };
        };
        alerts: Array<{
          type: 'deficiency' | 'excess' | 'imbalance';
          nutrient: string;
          severity: 'low' | 'medium' | 'high';
          message: string;
        }>;
        recommendations: string[];
        feedQuantityEstimation: FeedQuantityEstimation;
      }>(`/feed-stock/nutrition/analytics/dashboard?${params}`);
      
      if (!response.data || typeof response.data !== 'object') {
        throw new Error('Invalid response format: expected dashboard object');
      }
      
      // Check required properties
      const requiredProps = ['period', 'trends', 'analytics', 'dashboard'];
      for (const prop of requiredProps) {
        if (!(prop in response.data)) {
          throw new Error(`Invalid response format: missing ${prop} property`);
        }
      }
      
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async getNutritionSummary(date: string, location?: string): Promise<{
    date: string;
    location?: string;
    nutritionalSummary: DailyNutritionalSummary;
    nutritionPer100g: any;
    meetsMininumRequirements: boolean;
    components: FeedMixComponent[];
    totalQuantity: number;
    totalCost?: number;
    qualityScore: number;
    recommendations: string[];
  } | { message: string; date: string; location?: string }> {
    const params = new URLSearchParams();
    if (location) params.append('location', location);

    const response = await api.get<{
      date: string;
      location?: string;
      nutritionalSummary: DailyNutritionalSummary;
      nutritionPer100g: any;
      meetsMininumRequirements: boolean;
      components: FeedMixComponent[];
      totalQuantity: number;
      totalCost?: number;
      qualityScore: number;
      recommendations: string[];
    } | { message: string; date: string; location?: string }>(`/feed-stock/nutrition/nutrition-summary/${date}?${params}`);
    return response.data;
  },

  async getWeeklyReport(date: string, location?: string): Promise<{
    week: { start: string; end: string };
    location?: string;
    summary: {
      totalMixes: number;
      totalFeedUsed: number;
      totalCost: number;
      averageCostPerKg: number;
      averageQualityScore: number;
    };
    nutritionalAverages: {
      protein: number;
      fat: number;
      fiber: number;
      carbohydrate: number;
      moisture: number;
      dailyQuantity: number;
      dailyCalories: number;
    };
    dailyBreakdown: Array<{
      date: string;
      protein: number;
      fat: number;
      fiber: number;
      carbohydrate: number;
      quality: number;
      quantity: number;
      cost: number;
      approved: boolean;
    }>;
    feedComponents: Array<{
      feedStockId: string;
      name: string;
      totalQuantity: number;
      percentage: number;
    }>;
    recommendations: string[];
  }> {
    const params = new URLSearchParams();
    if (location) params.append('location', location);

    const response = await api.get<{
      week: { start: string; end: string };
      location?: string;
      summary: {
        totalMixes: number;
        totalFeedUsed: number;
        totalCost: number;
        averageCostPerKg: number;
        averageQualityScore: number;
      };
      nutritionalAverages: {
        protein: number;
        fat: number;
        fiber: number;
        carbohydrate: number;
        moisture: number;
        dailyQuantity: number;
        dailyCalories: number;
      };
      dailyBreakdown: Array<{
        date: string;
        protein: number;
        fat: number;
        fiber: number;
        carbohydrate: number;
        quality: number;
        quantity: number;
        cost: number;
        approved: boolean;
      }>;
      feedComponents: Array<{
        feedStockId: string;
        name: string;
        totalQuantity: number;
        percentage: number;
      }>;
      recommendations: string[];
    }>(`/feed-stock/nutrition/reports/weekly/${date}?${params}`);
    return response.data;
  },

  // Feed Mix Optimization
  async optimizeFeedMix(data: {
    targetDate: string;
    requirements: NutritionalRequirements;
    availableFeedStockIds: string[];
    targetQuantity?: number;
    optimizationGoal?: 'cost' | 'nutrition' | 'balanced';
    maxComponentPercentage?: number;
  }): Promise<OptimizedFeedMix> {
    const response = await api.post<OptimizedFeedMix>('/feed-stock/nutrition/optimize-mix', data);
    return response.data;
  },

  async createFromOptimization(data: {
    targetDate: string;
    requirements: NutritionalRequirements;
    availableFeedStockIds: string[];
    targetQuantity?: number;
    optimizationGoal?: 'cost' | 'nutrition' | 'balanced';
    maxComponentPercentage?: number;
    location?: string;
    operator?: string;
    notes?: string;
    birdCount?: number;
    autoApprove?: boolean;
    replaceExisting?: boolean;
    approvedBy?: string;
  }): Promise<{
    mix: DailyFeedMix;
    optimizedMix: FeedMixComponent[];
    nutritionalSummary: DailyNutritionalSummary;
    optimization: OptimizedFeedMix['optimization'];
  }> {
    const response = await api.post<{
      mix: DailyFeedMix;
      optimizedMix: FeedMixComponent[];
      nutritionalSummary: DailyNutritionalSummary;
      optimization: OptimizedFeedMix['optimization'];
    }>('/feed-stock/nutrition/daily-mixes/from-optimization', data);
    return response.data;
  },

  async recordMixDispense(
    mixId: string,
    data: { operator?: string; cageNumber?: string; notes?: string },
  ): Promise<DailyFeedMix> {
    const response = await api.post<DailyFeedMix>(
      `/feed-stock/nutrition/daily-mixes/${mixId}/dispense`,
      data,
    );
    return response.data;
  },

  getReportHistory: async (params: ReportHistoryParams) => {
    const searchParams = new URLSearchParams();
    if (params.location) searchParams.append('location', params.location);
    searchParams.append('startDate', params.startDate);
    searchParams.append('endDate', params.endDate);
    
    const response = await api.get(`/feed-stock/nutrition/reports/history?${searchParams.toString()}`);
    return response;
  },

  getScheduledReports: async (params: ScheduledReportParams) => {
    const searchParams = new URLSearchParams();
    if (params.location) searchParams.append('location', params.location);
    
    const response = await api.get(`/feed-stock/nutrition/reports/scheduled?${searchParams.toString()}`);
    return response;
  },

  generateReport: async (params: ReportGenerationParams) => {
    const response = await api.post('/feed-stock/nutrition/reports/generate', params);
    return response;
  },

  scheduleReport: async (data: ReportScheduleData) => {
    const response = await api.post('/feed-stock/nutrition/reports/schedule', data);
    return response;
  },
};

export default nutritionApi; 